// src/services/offlineTimeState.js
//
// Reconciles the server's active-time-entry list with punches still sitting in
// the outbox, so the clock page tells the truth while a crew is out of signal.
//
// Without this the sequence breaks in a way that costs real hours:
//
//   1. Crew clocks in at the gate — no signal, so it queues.
//   2. The page reloads active entries, gets the cached (pre-clock-in) list, and
//      shows them as clocked OUT.
//   3. Four hours later they try to clock out and there's nothing to clock out of.
//
// So a queued clock-in is folded in as a synthetic active entry, and a queued
// clock-out removes the entry it closes. The synthetic entry's id is the outbox
// placeholder (`local-<opId>`); when the crew later clocks out against it, the
// drain swaps that placeholder for the real server id once the clock-in lands.

import { getPending } from './outbox';

function pathOf(op) {
  return op.url || '';
}

/**
 * @param {Array}  entries  active entries as returned by the API (or cache)
 * @param {number} userId   current user
 * @returns {Array} entries with queued punches applied
 */
export async function applyQueuedTimeState(entries, userId) {
  const ops = await getPending();
  if (!ops.length) return entries;

  let result = [...(entries || [])];

  // Entry ids and job ids closed by a queued clock-out.
  const closedEntryIds = new Set();
  const closedJobIds = new Set();

  for (const op of ops) {
    if (op.kind === 'clock-out') {
      const m = pathOf(op).match(/^\/time-entries\/([\w-]+)\/clock-out$/);
      if (m) closedEntryIds.add(m[1]);
    } else if (op.kind === 'clock-out-job') {
      const m = pathOf(op).match(/^\/time-entries\/clock-out-job\/(\d+)$/);
      if (m) closedJobIds.add(Number(m[1]));
    }
  }

  // Fold in queued clock-ins as active entries.
  const synthetic = [];
  for (const op of ops) {
    if (op.kind === 'clock-in') {
      const data = op.data || {};
      synthetic.push({
        id: op.publishesPlaceholder,
        team_member_id: userId,
        job_id: data.job_id ?? null,
        work_order_id: data.work_order_id ?? null,
        phase_id: data.phase_id ?? null,
        work_type: data.work_type || 'Daily',
        clock_in: op.occurredAt,
        calc_clock_in: op.occurredAt,
        clock_out: null,
        on_break: false,
        is_paused: false,
        accumulated_seconds: 0,
        __queued: true,
      });
    } else if (op.kind === 'clock-in-crew') {
      const data = op.data || {};
      const ids = data.team_member_ids || [];
      ids.forEach((memberId, idx) => {
        synthetic.push({
          // Only the current user's row can be clocked out from this device, so
          // only that one gets the resolvable placeholder.
          id: memberId === userId ? op.publishesPlaceholder : `${op.publishesPlaceholder}-${idx}`,
          team_member_id: memberId,
          job_id: data.job_id ?? null,
          work_order_id: data.work_order_id ?? null,
          phase_id: data.phase_id ?? null,
          work_type: data.work_type || 'Field Work',
          clock_in: op.occurredAt,
          calc_clock_in: op.occurredAt,
          clock_out: null,
          clock_in_group: op.publishesPlaceholder,
          on_break: false,
          is_paused: false,
          accumulated_seconds: 0,
          __queued: true,
        });
      });
    }
  }

  result = result.concat(synthetic);

  // Drop anything a queued clock-out has already closed.
  result = result.filter((e) => {
    if (closedEntryIds.has(String(e.id))) return false;
    if (e.job_id != null && closedJobIds.has(Number(e.job_id))) return false;
    return true;
  });

  return result;
}

export default applyQueuedTimeState;
